import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { PageHeader } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Save, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function HomeCMS() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/cms/home").then(({ data }) => setC(data)).catch((e) => toast.error(formatError(e))); }, []);
  if (!c) return <div className="text-slate-400">Loading…</div>;

  const u = (k, v) => setC((d) => ({ ...d, [k]: v }));
  const upStat = (i, k, v) => setC((d) => ({ ...d, stats: d.stats.map((s, x) => x === i ? { ...s, [k]: v } : s) }));
  const upFeat = (i, k, v) => setC((d) => ({ ...d, features: d.features.map((s, x) => x === i ? { ...s, [k]: v } : s) }));

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/settings/key/home_cms", c); toast.success("Home page updated"); }
    catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Home Page CMS" subtitle="Edit the public landing page content">
        <Button onClick={save} disabled={saving} className="bg-[#6B1A24] hover:bg-[#52141C]" data-testid="cms-save-btn"><Save className="w-4 h-4 mr-1.5" />{saving ? "Saving…" : "Save changes"}</Button>
      </PageHeader>

      <div className="space-y-6 max-w-3xl">
        <Card title="Hero section">
          <Field label="Badge text"><Input value={c.hero_badge || ""} onChange={(e) => u("hero_badge", e.target.value)} data-testid="cms-hero-badge" /></Field>
          <Field label="Headline line 1"><Input value={c.hero_title_1 || ""} onChange={(e) => u("hero_title_1", e.target.value)} data-testid="cms-hero-title1" /></Field>
          <Field label="Headline line 2"><Input value={c.hero_title_2 || ""} onChange={(e) => u("hero_title_2", e.target.value)} data-testid="cms-hero-title2" /></Field>
          <Field label="Sub-headline"><Textarea rows={3} value={c.hero_subtitle || ""} onChange={(e) => u("hero_subtitle", e.target.value)} data-testid="cms-hero-subtitle" /></Field>
        </Card>

        <Card title="Hero stats">
          <div className="grid sm:grid-cols-3 gap-4">
            {(c.stats || []).map((s, i) => (
              <div key={i} className="space-y-2 border border-slate-100 rounded-lg p-3">
                <Input value={s.n} onChange={(e) => upStat(i, "n", e.target.value)} placeholder="12k+" data-testid={`cms-stat-n-${i}`} />
                <Input value={s.l} onChange={(e) => upStat(i, "l", e.target.value)} placeholder="Label" data-testid={`cms-stat-l-${i}`} />
              </div>
            ))}
          </div>
        </Card>

        <Card title="Feature cards">
          <div className="space-y-3">
            {(c.features || []).map((f, i) => (
              <div key={i} className="border border-slate-100 rounded-lg p-3 space-y-2">
                <Input value={f.title} onChange={(e) => upFeat(i, "title", e.target.value)} placeholder="Title" data-testid={`cms-feat-title-${i}`} />
                <Textarea rows={2} value={f.desc} onChange={(e) => upFeat(i, "desc", e.target.value)} placeholder="Description" data-testid={`cms-feat-desc-${i}`} />
              </div>
            ))}
          </div>
        </Card>

        <Card title="Bottom CTA">
          <Field label="CTA title"><Input value={c.cta_title || ""} onChange={(e) => u("cta_title", e.target.value)} data-testid="cms-cta-title" /></Field>
          <Field label="CTA subtitle"><Input value={c.cta_subtitle || ""} onChange={(e) => u("cta_subtitle", e.target.value)} data-testid="cms-cta-subtitle" /></Field>
        </Card>
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
      <div className="px-6 py-4 border-b border-slate-100"><h3 className="text-lg font-medium text-slate-800">{title}</h3></div>
      <div className="p-6 space-y-4">{children}</div>
    </div>
  );
}
function Field({ label, children }) {
  return <div className="flex flex-col gap-2"><Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>{children}</div>;
}
